import { FLAG_PURPOSES } from '../schemas/flag';
import { LIFECYCLE_ORDER } from '../schemas/lifecycle';

import type { FlagLifecycle, FlagListItem, FlagPurpose, FlagType } from '../schemas/flag';

/**
 * What the flags list shows, and what its chips are allowed to claim.
 *
 * Extracted from `FlagsPage` when a second chip row arrived. The page had
 * derived its counts from the loaded rows precisely so they could not disagree
 * with the table — but it counted lifecycles over the rows of the current tab
 * only, which knew nothing about a purpose filter. Add one and the chips start
 * promising more rows than the table can show.
 *
 * The rule here: a chip row counts the rows passing every filter **except its
 * own axis**. So a lifecycle chip answers "switching to me would show you this
 * many", which is both useful and true, while the numbers still reconcile with
 * the table for whichever chip is currently selected.
 *
 * Tabs are left out of it on purpose. `type` partitions the catalog rather than
 * narrowing it — the two kinds have disjoint readers (ADR 0009 §5) — and its
 * counts stay over every loaded row, as they always have.
 */

const ALL = 'all' as const;
type All = typeof ALL;

export interface FlagListFilters {
    readonly type: FlagType;
    readonly lifecycle: FlagLifecycle | All;
    readonly purpose: FlagPurpose | All;
    readonly search: string;
}

export interface FlagFacets {
    readonly visible: readonly FlagListItem[];
    readonly lifecycleCounts: ReadonlyMap<FlagLifecycle | All, number>;
    readonly purposeCounts: ReadonlyMap<FlagPurpose | All, number>;
}

const matchesType = (item: FlagListItem, type: FlagType) => item.definition.type === type;

const matchesLifecycle = (item: FlagListItem, lifecycle: FlagLifecycle | All) =>
    lifecycle === ALL || item.definition.lifecycle === lifecycle;

const matchesPurpose = (item: FlagListItem, purpose: FlagPurpose | All) =>
    purpose === ALL || item.definition.purpose === purpose;

/** Key and name only — the same two fields the search box has always looked at. */
const matchesSearch = (item: FlagListItem, needle: string) =>
    needle.length === 0 ||
    item.definition.key.toLowerCase().includes(needle) ||
    item.definition.name.toLowerCase().includes(needle);

/**
 * Every value seeded at zero, so a chip for a purpose nothing currently uses
 * renders as `0` rather than vanishing. A chip row that changes width as the
 * operator types is a chip row that is hard to aim at.
 */
function tally<T extends string>(
    values: readonly T[],
    items: readonly FlagListItem[],
    of: (item: FlagListItem) => T,
): ReadonlyMap<T | All, number> {
    const counts = new Map<T | All, number>(values.map((value) => [value, 0]));
    counts.set(ALL, items.length);

    for (const item of items) {
        const value = of(item);
        counts.set(value, (counts.get(value) ?? 0) + 1);
    }
    return counts;
}

export function flagFacets(rows: readonly FlagListItem[], filters: FlagListFilters): FlagFacets {
    const needle = filters.search.trim().toLowerCase();

    const ofType = rows.filter((item) => matchesType(item, filters.type));
    const searched = ofType.filter((item) => matchesSearch(item, needle));

    // Each base excludes exactly one axis — the one whose chips it counts.
    const withoutLifecycle = searched.filter((item) => matchesPurpose(item, filters.purpose));
    const withoutPurpose = searched.filter((item) => matchesLifecycle(item, filters.lifecycle));

    return {
        visible: withoutLifecycle.filter((item) => matchesLifecycle(item, filters.lifecycle)),
        lifecycleCounts: tally(
            LIFECYCLE_ORDER,
            withoutLifecycle,
            (item) => item.definition.lifecycle,
        ),
        purposeCounts: tally(FLAG_PURPOSES, withoutPurpose, (item) => item.definition.purpose),
    };
}
